// src/App.jsx
import React, { useState, useEffect, Suspense } from 'react';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  sendEmailVerification,
  updateProfile,
  signInWithPopup,
} from 'firebase/auth';
import { doc, setDoc, getDoc, addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { auth, db, googleProvider } from './firebase';
import { BrowserRouter, Routes, Route, useLocation, useParams, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { ProjectsProvider } from './context/ProjectsContext';
import { LeadsProvider } from './context/LeadsContext';
import { PortfolioProvider } from './context/PortfolioContext';
import { UIProvider } from './context/UIContext';
import { TeamSettingsProvider } from './context/TeamSettingsContext';
import { TutorialProvider } from './context/TutorialContext';
import { AgentChatProvider } from './context/AgentChatContext';
import { ToastProvider } from './components/Toast';
import Layout from './components/Layout';
import ErrorBoundary from './components/ErrorBoundary';
import { trackRouteChange } from './utils/performanceMonitor';

// Eagerly loaded — needed before auth resolves
import LoadingScreen from './components/LoadingScreen';

// Auth pages — eagerly loaded (small, shown before main app)
import LandingPage from './pages/auth/LandingPage';
import SignInPage from './pages/auth/SignInPage';
import SignUpPage from './pages/auth/SignUpPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import PrivacyPolicyPage from './pages/auth/PrivacyPolicyPage';
import TermsOfServicePage from './pages/auth/TermsOfServicePage';
import EmailVerificationPage from './pages/auth/EmailVerificationPage';

// Track route navigation timing via Firebase Performance
function RoutePerformanceTracker() {
  const location = useLocation();
  const traceRef = React.useRef(null);

  useEffect(() => {
    // Stop the previous route's trace (page has rendered)
    if (traceRef.current) traceRef.current.done();
    // Start a new trace for the route we just navigated to
    traceRef.current = trackRouteChange(location.pathname);
  }, [location.pathname]);

  return null;
}

// App pages — lazy-loaded (only fetched when the user navigates to them)
const DashboardPage = React.lazy(() => import('./pages/DashboardPage'));
const LeadsPage = React.lazy(() => import('./pages/LeadsPage'));
const FlipAnalyzerPage = React.lazy(() => import('./pages/FlipAnalyzerPage'));
const BRRRAnalyzerPage = React.lazy(() => import('./pages/BRRRAnalyzerPage'));
const LoanCalculatorPage = React.lazy(() => import('./pages/LoanCalculatorPage'));
const RehabEstimatorPage = React.lazy(() => import('./pages/RehabEstimatorPage'));
const CompsPage = React.lazy(() => import('./pages/CompsPage'));
const ProjectDetailPage = React.lazy(() => import('./pages/ProjectDetailPage'));
const SchedulerPage = React.lazy(() => import('./pages/SchedulerPage'));
const TasksPage = React.lazy(() => import('./pages/TasksPage'));
const ExpensesPage = React.lazy(() => import('./pages/ExpensesPage'));
const PhotosPage = React.lazy(() => import('./pages/PhotosPage'));
const EscrowDocsPage = React.lazy(() => import('./pages/EscrowDocsPage'));
const PropertyReportsPage = React.lazy(() => import('./pages/PropertyReportsPage'));
const ContractsPage = React.lazy(() => import('./pages/ContractsPage'));
const ListingManagementPage = React.lazy(() => import('./pages/ListingManagementPage'));
const RealTimeAnalysisPage = React.lazy(() => import('./pages/RealTimeAnalysisPage'));
const PortfolioPage = React.lazy(() => import('./pages/PortfolioPage'));
const ReportsPage = React.lazy(() => import('./pages/ReportsPage'));
const TeamPage = React.lazy(() => import('./pages/TeamPage'));
const SettingsPage = React.lazy(() => import('./pages/SettingsPage'));
const ApprovalsPage = React.lazy(() => import('./pages/ApprovalsPage'));
const PipelinePage = React.lazy(() => import('./pages/PipelinePage'));
const AcquisitionDashboardPage = React.lazy(() => import('./pages/AcquisitionDashboardPage'));
const LeadDetailPage = React.lazy(() => import('./pages/LeadDetailPage'));
const RehabDashboardPage = React.lazy(() => import('./pages/RehabDashboardPage'));
const RehabPipelinePage = React.lazy(() => import('./pages/RehabPipelinePage'));
const SellingDashboardPage = React.lazy(() => import('./pages/SellingDashboardPage'));
const SellingPipelinePage = React.lazy(() => import('./pages/SellingPipelinePage'));
const AllLeadsPage = React.lazy(() => import('./pages/AllLeadsPage'));
const AllRehabProjectsPage = React.lazy(() => import('./pages/AllRehabProjectsPage'));
const AllSellingProjectsPage = React.lazy(() => import('./pages/AllSellingProjectsPage'));
const PropertiesPage = React.lazy(() => import('./pages/PropertiesPage'));
const FindLeadsPage = React.lazy(() => import('./pages/FindLeadsPage'));
const NotFoundPage = React.lazy(() => import('./pages/NotFoundPage'));

// Redirect /join/:code → /team with code in location state
function JoinRedirect() {
  const { code } = useParams();
  return <Navigate to="/team" state={{ joinCode: code?.toUpperCase() }} replace />;
}

export default function App() {
  const [currentUser, setCurrentUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [authPage, setAuthPage] = useState(() => {
    const path = window.location.pathname;
    if (path === '/privacy') return 'privacy';
    if (path === '/terms') return 'terms';
    return 'landing';
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Map Firebase error codes to user-friendly messages
  const getFriendlyAuthError = (err) => {
    const code = err.code;
    const map = {
      'auth/email-already-in-use': 'An account with this email already exists.',
      'auth/invalid-email': 'Please enter a valid email address.',
      'auth/user-disabled': 'This account has been disabled. Contact support.',
      'auth/user-not-found': 'No account found with this email.',
      'auth/wrong-password': 'Incorrect password. Please try again.',
      'auth/invalid-credential': 'Invalid email or password. Please try again.',
      'auth/too-many-requests': 'Too many attempts. Please try again later.',
      'auth/weak-password': 'Password is too weak. Use at least 6 characters.',
      'auth/network-request-failed': 'Network error. Check your connection and try again.',
      'auth/popup-closed-by-user': null, // handled separately
    };
    return map[code] || 'Something went wrong. Please try again.';
  };

  // Redirect URL to root if on a protected path while not authenticated
  const redirectIfProtectedPath = () => {
    const path = window.location.pathname;
    const publicPaths = ['/', '/privacy', '/terms'];
    if (!publicPaths.includes(path)) {
      window.history.replaceState({}, '', '/');
    }
  };

  // Listen to auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        redirectIfProtectedPath();
      }
      setCurrentUser(user);
      setAuthLoading(false);
    });
    return unsubscribe;
  }, []);

  // Login handler
  const handleLogin = async (email, password) => {
    setIsLoading(true);
    setError('');
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (err) {
      setError(getFriendlyAuthError(err));
    }
    setIsLoading(false);
  };

  // Signup handler with team name and email verification
  const handleSignup = async (name, email, password, teamName, teamCode) => {
    setIsLoading(true);
    setError('');
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      await updateProfile(result.user, { displayName: name });

      // If joining a team with code, look up the team first
      let teamOwnerId = null;
      let teamOwnerData = null;

      if (teamCode && teamCode.length === 8) {
        // Normalize to uppercase — Firestore queries are case-sensitive
        const normalizedCode = teamCode.toUpperCase();

        // Direct document lookup by ID (team code IS the doc ID)
        const directDoc = await getDoc(doc(db, 'teamCodes', normalizedCode));
        if (directDoc.exists()) {
          teamOwnerData = directDoc.data();
          teamOwnerId = teamOwnerData.ownerId;
        }

        if (!teamOwnerId) {
          // Delete the just-created auth user so they can try again
          await result.user.delete();
          setError('Team code not found. Please check the code and try again.');
          setIsLoading(false);
          return;
        }
      }

      // Save user data to Firestore
      await setDoc(doc(db, 'users', result.user.uid), {
        name: name,
        email: email,
        teamName: teamName || '',
        teamOwnerId: teamOwnerId || null,
        createdAt: serverTimestamp(),
        role: teamOwnerId ? 'member' : 'owner',
      });

      // If NOT joining a team, register team code now so it's available immediately
      if (!teamOwnerId) {
        // Generate a random 8-character alphanumeric team code (not derived from UID)
        const myTeamCode = Math.random().toString(36).substring(2, 10).toUpperCase();
        await setDoc(doc(db, 'teamCodes', myTeamCode), {
          code: myTeamCode,
          ownerId: result.user.uid,
          ownerEmail: email,
          ownerName: name,
          teamName: teamName || 'Team Workspace',
          companyName: '',
          companyLogo: '',
          createdAt: serverTimestamp(),
        });
      }

      // If joining a team, create membership and add to team members
      if (teamOwnerId && teamOwnerData) {
        await addDoc(collection(db, 'teamMemberships'), {
          memberEmail: email,
          memberName: name,
          memberUid: result.user.uid,
          teamOwnerId: teamOwnerId,
          teamCode: teamCode.toUpperCase(),
          teamName: teamOwnerData.teamName || 'Team Workspace',
          ownerName: teamOwnerData.ownerName,
          ownerEmail: teamOwnerData.ownerEmail,
          role: 'Member',
          status: 'active',
          joinedAt: serverTimestamp(),
        });

        await addDoc(collection(db, 'teamMembers'), {
          ownerId: teamOwnerId,
          memberUid: result.user.uid,
          name: name,
          email: email,
          role: 'Member',
          avatar: name[0].toUpperCase(),
          joinedAt: serverTimestamp(),
        });
      }

      await sendEmailVerification(result.user);
    } catch (err) {
      setError(getFriendlyAuthError(err));
    }
    setIsLoading(false);
  };

  // Password reset handler
  const handleResetPassword = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
      return { success: true };
    } catch (err) {
      return { success: false, error: getFriendlyAuthError(err) };
    }
  };

  // Google sign-in / sign-up handler
  const handleGoogleAuth = async (joinMode, teamName, teamCode) => {
    setIsLoading(true);
    setError('');
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;

      // Check if user already exists in Firestore (returning user)
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        // Existing user — just sign in, nothing else to do
        setIsLoading(false);
        return;
      }

      // New user — create Firestore profile
      const name = user.displayName || user.email.split('@')[0];
      const email = user.email;

      // If joining a team with code, validate it
      let teamOwnerId = null;
      let teamOwnerData = null;

      if (teamCode && teamCode.length === 8) {
        const normalizedCode = teamCode.toUpperCase();
        const directDoc = await getDoc(doc(db, 'teamCodes', normalizedCode));
        if (directDoc.exists()) {
          teamOwnerData = directDoc.data();
          teamOwnerId = teamOwnerData.ownerId;
        }

        if (!teamOwnerId) {
          // Delete the just-created auth user so they can try again
          await user.delete();
          setError('Team code not found. Please check the code and try again.');
          setIsLoading(false);
          return;
        }
      }

      // Save user data to Firestore
      await setDoc(doc(db, 'users', user.uid), {
        name: name,
        email: email,
        teamName: teamName || '',
        teamOwnerId: teamOwnerId || null,
        createdAt: serverTimestamp(),
        role: teamOwnerId ? 'member' : 'owner',
      });

      // If NOT joining a team, register team code
      if (!teamOwnerId) {
        const myTeamCode = Math.random().toString(36).substring(2, 10).toUpperCase();
        await setDoc(doc(db, 'teamCodes', myTeamCode), {
          code: myTeamCode,
          ownerId: user.uid,
          ownerEmail: email,
          ownerName: name,
          teamName: teamName || 'Team Workspace',
          companyName: '',
          companyLogo: '',
          createdAt: serverTimestamp(),
        });
      }

      // If joining a team, create membership records
      if (teamOwnerId && teamOwnerData) {
        await addDoc(collection(db, 'teamMemberships'), {
          memberEmail: email,
          memberName: name,
          memberUid: user.uid,
          teamOwnerId: teamOwnerId,
          teamCode: teamCode.toUpperCase(),
          teamName: teamOwnerData.teamName || 'Team Workspace',
          ownerName: teamOwnerData.ownerName,
          ownerEmail: teamOwnerData.ownerEmail,
          role: 'Member',
          status: 'active',
          joinedAt: serverTimestamp(),
        });

        await addDoc(collection(db, 'teamMembers'), {
          ownerId: teamOwnerId,
          memberUid: user.uid,
          name: name,
          email: email,
          role: 'Member',
          avatar: name[0].toUpperCase(),
          joinedAt: serverTimestamp(),
        });
      }
    } catch (err) {
      // User closed the popup — don't show an error
      if (err.code === 'auth/popup-closed-by-user') {
        setIsLoading(false);
        return;
      }
      setError(getFriendlyAuthError(err));
    }
    setIsLoading(false);
  };

  // Logout handler
  const handleLogout = async () => {
    await signOut(auth);
    setAuthPage('landing');
    window.history.replaceState({}, '', '/');
  };

  // Show loading screen
  if (authLoading) return <LoadingScreen />;

  // Show auth pages if not logged in (wrapped in ErrorBoundary for crash protection)
  if (!currentUser) {
    const renderAuthPage = () => {
      switch (authPage) {
        case 'signup':
          return (
            <SignUpPage
              setAuthPage={setAuthPage}
              onSignup={handleSignup}
              onGoogleAuth={handleGoogleAuth}
              error={error}
              isLoading={isLoading}
            />
          );
        case 'forgot-password':
          return <ForgotPasswordPage setAuthPage={setAuthPage} onReset={handleResetPassword} />;
        case 'signin':
          return (
            <SignInPage
              setAuthPage={setAuthPage}
              onLogin={handleLogin}
              onGoogleAuth={handleGoogleAuth}
              error={error}
              isLoading={isLoading}
            />
          );
        case 'privacy':
          return <PrivacyPolicyPage setAuthPage={setAuthPage} />;
        case 'terms':
          return <TermsOfServicePage setAuthPage={setAuthPage} />;
        default:
          return <LandingPage setAuthPage={setAuthPage} />;
      }
    };

    return <ErrorBoundary key={authPage}>{renderAuthPage()}</ErrorBoundary>;
  }

  // User is authenticated but email not verified — show verification gate
  // Skip for Google users (providerData[0].providerId === 'google.com') since Google already verifies
  const isGoogleUser = currentUser?.providerData?.some((p) => p.providerId === 'google.com');
  if (currentUser && !currentUser.emailVerified && !isGoogleUser) {
    return (
      <ErrorBoundary>
        <EmailVerificationPage
          user={currentUser}
          onResendEmail={async () => {
            try {
              await sendEmailVerification(currentUser);
            } catch (err) {
              throw err;
            }
          }}
          onLogout={handleLogout}
          onContinue={() => {
            // User already reloaded by EmailVerificationPage — just force re-render
            setCurrentUser({ ...auth.currentUser });
          }}
        />
      </ErrorBoundary>
    );
  }

  // User is authenticated and verified - show the main app with React Router
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <RoutePerformanceTracker />
        <ToastProvider>
          <AuthProvider firebaseUser={currentUser} onLogout={handleLogout}>
            <TeamSettingsProvider>
              <AgentChatProvider>
                <UIProvider>
                  <ProjectsProvider>
                    <LeadsProvider>
                      <PortfolioProvider>
                        <TutorialProvider firebaseUser={currentUser}>
                          <Suspense fallback={<LoadingScreen />}>
                            <Routes>
                              <Route element={<Layout />}>
                                <Route path="/" element={<DashboardPage />} />
                                <Route path="/properties" element={<PropertiesPage />} />
                                <Route path="/acquisition" element={<AcquisitionDashboardPage />} />
                                <Route path="/acquisition/all" element={<AllLeadsPage />} />
                                <Route path="/find-leads" element={<FindLeadsPage />} />
                                <Route path="/leads" element={<LeadsPage />} />
                                <Route path="/leads/:leadId" element={<LeadDetailPage />} />
                                <Route path="/flip-analyzer" element={<FlipAnalyzerPage />} />
                                <Route path="/brrrr-analyzer" element={<BRRRAnalyzerPage />} />
                                <Route path="/loan-calculator" element={<LoanCalculatorPage />} />
                                <Route path="/rehab-estimator" element={<RehabEstimatorPage />} />
                                <Route path="/comps" element={<CompsPage />} />
                                <Route
                                  path="/projects"
                                  element={<Navigate to="/properties" replace />}
                                />
                                <Route
                                  path="/projects/:projectId"
                                  element={<ProjectDetailPage />}
                                />
                                <Route path="/pipeline" element={<PipelinePage />} />
                                <Route path="/rehab" element={<RehabDashboardPage />} />
                                <Route path="/rehab/all" element={<AllRehabProjectsPage />} />
                                <Route path="/rehab-pipeline" element={<RehabPipelinePage />} />
                                <Route path="/selling" element={<SellingDashboardPage />} />
                                <Route path="/selling/all" element={<AllSellingProjectsPage />} />
                                <Route path="/selling-pipeline" element={<SellingPipelinePage />} />
                                <Route
                                  path="/selling/listings"
                                  element={<ListingManagementPage />}
                                />
                                <Route path="/scheduler" element={<SchedulerPage />} />
                                <Route path="/tasks" element={<TasksPage />} />
                                <Route path="/expenses" element={<ExpensesPage />} />
                                <Route path="/photos" element={<PhotosPage />} />
                                <Route path="/escrow-docs" element={<EscrowDocsPage />} />
                                <Route path="/property-reports" element={<PropertyReportsPage />} />
                                <Route path="/contracts" element={<ContractsPage />} />
                                <Route path="/analysis" element={<RealTimeAnalysisPage />} />
                                <Route path="/portfolio" element={<PortfolioPage />} />
                                <Route path="/reports" element={<ReportsPage />} />
                                <Route path="/team" element={<TeamPage />} />
                                <Route path="/join/:code" element={<JoinRedirect />} />
                                <Route path="/approvals" element={<ApprovalsPage />} />
                                <Route path="/settings" element={<SettingsPage />} />
                                <Route path="*" element={<NotFoundPage />} />
                              </Route>
                            </Routes>
                          </Suspense>
                        </TutorialProvider>
                      </PortfolioProvider>
                    </LeadsProvider>
                  </ProjectsProvider>
                </UIProvider>
              </AgentChatProvider>
            </TeamSettingsProvider>
          </AuthProvider>
        </ToastProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
